import React, { useState, useMemo, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { SimpleDropdown } from "./ui/simple-dropdown";
import ChakraDatePicker from "./ui/chakra-datepicker";
import { X, Save, User, Mail, Phone, Briefcase, IdCard } from "lucide-react";
import { toast } from "react-hot-toast";
import { CreateParentRequest } from "../api/types";
import { educationAPI } from "../api/axiosClient";
import { IMaskInput } from "react-imask";

interface CreatedParent {
  id: string;
  fullName: string;
  phoneCode: string;
  phoneNumber: string;
}

interface ParentCreateFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onParentCreated?: (parent: CreatedParent) => void;
}

const ParentCreateForm: React.FC<ParentCreateFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onParentCreated,
}) => {
  const [formData, setFormData] = useState<CreateParentRequest>({
    phoneCode: "90",
    phoneNumber: "",
    email: "",
    firstName: "",
    lastName: "",
    identityNumber: "",
    occupation: "",
    birthDate: "",
  });
const [existingParentId, setExistingParentId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const [isFormEnabled, setIsFormEnabled] = useState(false);
  const [hasParentData, setHasParentData] = useState(false);
  const [canEditParentFields, setCanEditParentFields] = useState(false);
  const [existingUserId, setExistingUserId] = useState<string | null>(null);

  const [selectedCountryCode, setSelectedCountryCode] = useState<{
    id: string | number;
    label: string;
    value: string | number;
  } | null>({
    id: "90",
    label: "🇹🇷 +90",
    value: "90",
  });

  // Country codes for phone dropdown
  const countryCodes = useMemo(
    () => [
      { code: "90", name: "Türkiye (+90)", flag: "🇹🇷" },
      { code: "1", name: "USA (+1)", flag: "🇺🇸" },
      { code: "44", name: "UK (+44)", flag: "🇬🇧" },
      { code: "49", name: "Germany (+49)", flag: "🇩🇪" },
      { code: "33", name: "France (+33)", flag: "🇫🇷" },
      { code: "39", name: "Italy (+39)", flag: "🇮🇹" },
      { code: "34", name: "Spain (+34)", flag: "🇪🇸" },
      { code: "31", name: "Netherlands (+31)", flag: "🇳🇱" },
      { code: "32", name: "Belgium (+32)", flag: "🇧🇪" },
      { code: "46", name: "Sweden (+46)", flag: "🇸🇪" },
      { code: "47", name: "Norway (+47)", flag: "🇳🇴" },
      { code: "45", name: "Denmark (+45)", flag: "🇩🇰" },
      { code: "358", name: "Finland (+358)", flag: "🇫🇮" },
      { code: "7", name: "Russia (+7)", flag: "🇷🇺" },
      { code: "86", name: "China (+86)", flag: "🇨🇳" },
      { code: "81", name: "Japan (+81)", flag: "🇯🇵" },
      { code: "82", name: "South Korea (+82)", flag: "🇰🇷" },
      { code: "91", name: "India (+91)", flag: "🇮🇳" },
      { code: "971", name: "UAE (+971)", flag: "🇦🇪" },
      { code: "966", name: "Saudi Arabia (+966)", flag: "🇸🇦" },
      { code: "20", name: "Egypt (+20)", flag: "🇪🇬" },
      { code: "27", name: "South Africa (+27)", flag: "🇿🇦" },
      { code: "55", name: "Brazil (+55)", flag: "🇧🇷" },
      { code: "54", name: "Argentina (+54)", flag: "🇦🇷" },
      { code: "52", name: "Mexico (+52)", flag: "🇲🇽" },
    ],
    [],
  );

  // Dynamic phone mask based on country code
  const getPhoneMask = (countryCode: string): string => {
    switch (countryCode) {
      case "90": // Türkiye
        return "(000) 000-0000";
      case "1": // USA
        return "(000) 000-0000";
      case "44": // UK
        return "0000 000000";
      case "49": // Germany
        return "000 00000000";
      case "33": // France
        return "0 00 00 00 00";
      case "39": // Italy
        return "000 000 0000";
      case "34": // Spain
        return "000 000 000";
      case "31": // Netherlands
        return "0 00000000";
      case "32": // Belgium
        return "000 000 000";
      case "46": // Sweden
        return "000 000 000";
      case "47": // Norway
        return "000 00 000";
      case "45": // Denmark
        return "00 00 00 00";
      case "358": // Finland
        return "000 000 0000";
      case "7": // Russia
        return "000 000-00-00";
      case "86": // China
        return "000 0000 0000";
      case "81": // Japan
        return "00-0000-0000";
      case "82": // South Korea
        return "000-0000-0000";
      case "91": // India
        return "00000 00000";
      case "971": // UAE
        return "000 000 000";
      case "966": // Saudi Arabia
        return "000 000 000";
      case "20": // Egypt
        return "000 000 0000";
      case "27": // South Africa
        return "000 000 0000";
      case "55": // Brazil
        return "(00) 00000-0000";
      case "54": // Argentina
        return "000 000-0000";
      case "52": // Mexico
        return "000 000 0000";
      default:
        return "0000000000";
    }
  };

  // Get placeholder based on country code
  const getPhonePlaceholder = (countryCode: string): string => {
    switch (countryCode) {
      case "90":
        return "(5XX) XXX-XXXX";
      case "1":
        return "(XXX) XXX-XXXX";
      case "44":
        return "XXXX XXXXXX";
      case "49":
        return "XXX XXXXXXXX";
      case "33":
        return "X XX XX XX XX";
      case "39":
        return "XXX XXX XXXX";
      case "34":
        return "XXX XXX XXX";
      case "31":
        return "X XXXXXXXX";
      case "32":
        return "XXX XXX XXX";
      case "46":
        return "XXX XXX XXX";
      case "47":
        return "XXX XX XXX";
      case "45":
        return "XX XX XX XX";
      case "358":
        return "XXX XXX XXXX";
      case "7":
        return "XXX XXX-XX-XX";
      case "86":
        return "XXX XXXX XXXX";
      case "81":
        return "XX-XXXX-XXXX";
      case "82":
        return "XXX-XXXX-XXXX";
      case "91":
        return "XXXXX XXXXX";
      case "971":
        return "XXX XXX XXX";
      case "966":
        return "XXX XXX XXX";
      case "20":
        return "XXX XXX XXXX";
      case "27":
        return "XXX XXX XXXX";
      case "55":
        return "(XX) XXXXX-XXXX";
      case "54":
        return "XXX XXX-XXXX";
      case "52":
        return "XXX XXX XXXX";
      default:
        return "XXXXXXXXXXXX";
    }
  };

  // Reset form state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setIsFormEnabled(false);
      setHasParentData(false);
      setCanEditParentFields(false);
      setExistingUserId(null);
      setFormData({
        phoneCode: "90",
        phoneNumber: "",
        email: "",
        firstName: "",
        lastName: "",
        identityNumber: "",
        occupation: "",
        birthDate: "",
      });
      setErrors({});
      setIsCheckingPhone(false);
      setSelectedCountryCode({
        id: "90",
        label: "🇹🇷 +90",
        value: "90",
      });
    }
  }, [isOpen]);

  const handleInputChange = (field: keyof CreateParentRequest, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Check parent by phone number when input loses focus
  const handlePhoneBlur = async () => {
    // Only check if phone number has minimum length (e.g., 10 digits)
    if (
      formData.phoneCode &&
      formData.phoneNumber &&
      formData.phoneNumber.length >= 10
    ) {
      setIsCheckingPhone(true);

      try {
        const response = await educationAPI.checkParent(
          formData.phoneCode || "90",
          formData.phoneNumber || "",
        );

        if (response.success && response.data?.exists === true) {
          // User exists - fill form with user data
          const userData = response.data;
          console.log("User found:", userData);

          // Store userId for later use
          setExistingUserId(userData.userId || null);
          setExistingParentId(userData.parent?.id || null); 

          const hasParent = !!userData.parent;
          setHasParentData(hasParent);

          // If parent exists, disable all fields. If not, only firstName, lastName, email should be disabled
          setIsFormEnabled(false); // This disables firstName, lastName, email

          // If parent doesn't exist, parent fields should be enabled so user can fill them
          setCanEditParentFields(!hasParent);

          // Parse birthDate from ISO string to date input format (YYYY-MM-DD) if parent exists
          let birthDateFormatted = "";
          if (userData.parent?.birthDate) {
            try {
              const birthDate = new Date(userData.parent.birthDate);
              birthDateFormatted = birthDate.toISOString().split("T")[0];
            } catch (e) {
              console.error("Error parsing birthDate:", e);
            }
          }

          // Pre-fill the form with user data
          setFormData((prev) => ({
            ...prev,
            firstName: userData.firstName || prev.firstName,
            lastName: userData.lastName || prev.lastName,
            email: userData.email || prev.email,
            // Fill parent data if parent exists, otherwise clear them (user can fill them)
            identityNumber: userData.parent?.identityNumber || "",
            occupation: userData.parent?.occupation || "",
            birthDate: birthDateFormatted || "",
          }));
        } else {
          // User not found (exists: false) - enable form and reset to initial state
          setIsFormEnabled(true);
          setHasParentData(false);
          setCanEditParentFields(true);
          setExistingUserId(null);
          // Reset form to initial empty state
          setFormData((prev) => ({
            ...prev,
            email: "",
            firstName: "",
            lastName: "",
            identityNumber: "",
            occupation: "",
            birthDate: "",
          }));
          // Keep phone fields as they are
        }
      } catch (error: any) {
        // On error, enable form (assume user doesn't exist and allow creation) and reset to initial state
        setIsFormEnabled(true);
        setHasParentData(false);
        setCanEditParentFields(true);
        setExistingUserId(null);
        setExistingParentId(null);
        // Reset form to initial empty state
        setFormData((prev) => ({
          ...prev,
          email: "",
          firstName: "",
          lastName: "",
          identityNumber: "",
          occupation: "",
          birthDate: "",
        }));
        // Keep phone fields as they are
        console.log(
          "Parent check:",
          error.response?.data?.message || "Parent not found",
        );
      } finally {
        setIsCheckingPhone(false);
      }
    } else {
      // Reset form enabled state if phone number is too short
      setIsFormEnabled(false);
      setHasParentData(false);
      setCanEditParentFields(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    console.log("Validating form data:", formData);

    if (!formData.firstName?.trim()) {
      newErrors.firstName = "Ad alanı zorunludur";
    }

    if (!formData.lastName?.trim()) {
      newErrors.lastName = "Soyad alanı zorunludur";
    }

    if (!formData.email?.trim()) {
      newErrors.email = "E-posta alanı zorunludur";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Geçerli bir e-posta adresi girin";
    }

    if (!formData.phoneNumber?.trim()) {
      newErrors.phoneNumber = "Telefon numarası alanı zorunludur";
    }

    if (!formData.identityNumber?.trim()) {
      newErrors.identityNumber = "TC Kimlik No alanı zorunludur";
    } else if (!/^\d{11}$/.test(formData.identityNumber.replace(/\D/g, ""))) {
      newErrors.identityNumber = "TC Kimlik No 11 haneli olmalıdır";
    }

    if (!formData.occupation?.trim()) {
      newErrors.occupation = "Meslek alanı zorunludur";
    }

    console.log("Validation errors:", newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      let parentId: string | null = null;

      if (existingUserId) {
        const birthDateIso = formData.birthDate
          ? new Date(formData.birthDate + "T00:00:00.000Z").toISOString()
          : null;

        await educationAPI.addParent(
          existingUserId,
          formData.identityNumber || "",
          formData.occupation || "",
          birthDateIso,
        );

        parentId = existingParentId;

        if (!parentId) {
          const recheck = await educationAPI.checkParent(
            formData.phoneCode || "90",
            formData.phoneNumber || "",
          );
          parentId = recheck?.data?.parent?.id || null;
        }

        toast.success("Veli başarıyla eklendi");
      } else {
        const submitData = {
          phoneCode: formData.phoneCode,
          phoneNumber: formData.phoneNumber,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          identityNumber: formData.identityNumber,
          occupation: formData.occupation,
          birthDate: formData.birthDate
            ? new Date(formData.birthDate + "T00:00:00.000Z").toISOString()
            : null,
        };

        const response = await educationAPI.createParent(submitData);

        parentId =
          response?.data?.id ??
          response?.data?.data?.id ??
          response?.data?.parent?.id ??
          null;

        if (!parentId) {
          const recheck = await educationAPI.checkParent(
            formData.phoneCode || "90",
            formData.phoneNumber || "",
          );
          parentId = recheck?.data?.parent?.id || null;
        }

        toast.success("Veli başarıyla eklendi");
      }

      if (onParentCreated && parentId) {
        onParentCreated({
          id: parentId,
          fullName: `${formData.firstName || ""} ${formData.lastName || ""}`.trim(),
          phoneCode: String(formData.phoneCode || ""),
          phoneNumber: String(formData.phoneNumber || ""),
        });
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error creating parent:", error);
      toast.error(error.response?.data?.message || "Veli eklenirken hata oluştu");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="text-xl font-semibold">
              Yeni Veli Ekle
            </CardTitle>
            <CardDescription>Veli bilgilerini girin</CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Telefon Numarası */}
              <div>
                <Label
                  htmlFor="phoneNumber"
                  className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Telefon Numarası <span className="text-red-500 ml-1">*</span>
                </Label>
                <div className="flex">
                  <SimpleDropdown
                    options={countryCodes.map((country) => ({
                      id: country.code,
                      label: `${country.flag} +${country.code}`,
                      value: country.code,
                    }))}
                    value={selectedCountryCode}
                    onChange={(option) => {
                      if (option) {
                        setSelectedCountryCode(option);
                        handleInputChange("phoneCode", String(option.value));
                        // Clear phone number when country code changes
                        handleInputChange("phoneNumber", "");
                      }
                    }}
                    placeholder="Ülke kodu"
                    className="w-32 mr-2"
                  />
                  <IMaskInput
                    mask={getPhoneMask(formData.phoneCode || "90")}
                    value={formData.phoneNumber || ""}
                    onAccept={(value: string) => {
                      // Sadece rakamları al, maskeleme karakterlerini temizle
                      const cleanNumber = value.replace(/\D/g, "");
                      handleInputChange("phoneNumber", cleanNumber);
                    }}
                    onBlur={handlePhoneBlur}
                    placeholder={getPhonePlaceholder(
                      formData.phoneCode || "90",
                    )}
                    className={`flex-1 text-left text-sm font-mono px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400 ${errors.phoneNumber ? "border-red-500" : ""}`}
                    inputMode="numeric"
                  />
                </div>
                {errors.phoneNumber && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.phoneNumber}
                  </p>
                )}
              </div>

              {/* E-posta */}
              <div>
                <Label
                  htmlFor="email"
                  className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  E-posta <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email || ""}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className={errors.email ? "border-red-500" : ""}
                  placeholder="E-posta adresini girin"
                  disabled={!isFormEnabled}
                />
                {errors.email && (
                  <p className="text-sm text-red-600 mt-1">{errors.email}</p>
                )}
              </div>

              {/* Ad */}
              <div>
                <Label
                  htmlFor="firstName"
                  className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                >
                  <User className="w-4 h-4 mr-2" />
                  Ad <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  value={formData.firstName || ""}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  className={errors.firstName ? "border-red-500" : ""}
                  placeholder="Adını girin"
                  disabled={!isFormEnabled}
                />
                {errors.firstName && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.firstName}
                  </p>
                )}
              </div>

              {/* Soyad */}
              <div>
                <Label
                  htmlFor="lastName"
                  className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                >
                  <User className="w-4 h-4 mr-2" />
                  Soyad <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  value={formData.lastName || ""}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  className={errors.lastName ? "border-red-500" : ""}
                  placeholder="Soyadını girin"
                  disabled={!isFormEnabled}
                />
                {errors.lastName && (
                  <p className="text-sm text-red-600 mt-1">{errors.lastName}</p>
                )}
              </div>

              {/* TC Kimlik No */}
              <div>
                <Label
                  htmlFor="identityNumber"
                  className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                >
                  <IdCard className="w-4 h-4 mr-2" />
                  TC Kimlik No <span className="text-red-500 ml-1">*</span>
                </Label>
                <IMaskInput
                  id="identityNumber"
                  type="text"
                  mask="00000000000"
                  placeholder="12345678901"
                  value={formData.identityNumber || ""}
                  onAccept={(value: string) =>
                    handleInputChange("identityNumber", value)
                  }
                  className={`w-full text-left text-sm font-mono px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400 ${errors.identityNumber ? "border-red-500" : ""} ${hasParentData || !canEditParentFields ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  inputMode="numeric"
                  disabled={hasParentData || !canEditParentFields}
                />
                {errors.identityNumber && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.identityNumber}
                  </p>
                )}
              </div>

              {/* Meslek */}
              <div>
                <Label
                  htmlFor="occupation"
                  className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Meslek <span className="text-red-500 ml-1">*</span>
                </Label>
                <Input
                  id="occupation"
                  type="text"
                  value={formData.occupation || ""}
                  onChange={(e) =>
                    handleInputChange("occupation", e.target.value)
                  }
                  className={errors.occupation ? "border-red-500" : ""}
                  placeholder="Mesleğini girin"
                  disabled={hasParentData || !canEditParentFields}
                />
                {errors.occupation && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.occupation}
                  </p>
                )}
              </div>

              {/* Doğum Tarihi */}
              <div className="event-endsAt-fix">
                <Label
                  htmlFor="birthDate"
                  className="text-sm font-medium text-gray-700 mb-2 flex items-center"
                >
                  <User className="w-4 h-4 mr-2" />
                  Doğum Tarihi
                </Label>
                <ChakraDatePicker
                  value={formData.birthDate || ""}
                  onChange={(value) => handleInputChange("birthDate", value)}
                  placeholder="Doğum tarihi seçin"
                  hasError={!!errors.birthDate}
                  disabled={hasParentData || !canEditParentFields}
                  maxDate={new Date()}
                />
                {errors.birthDate && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.birthDate}
                  </p>
                )}
                   <style>{`
                        .event-endsAt-fix .react-datepicker__close-icon {
                          right: 34px;
                          top: 50%;
                          transform: translateY(-50%);
                          height: 100%;
                          display: flex;
                          align-items: center;
                        }
                        .event-endsAt-fix .react-datepicker__close-icon::after {
                          position: static;
                          transform: none;
                          margin: 0;
                        }
                      `}</style>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || isCheckingPhone}
                className="bg-[#9B51E0] hover:bg-[#8B4AD9] text-white"
              >
                {isSubmitting || isCheckingPhone ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isCheckingPhone
                      ? "Kontrol ediliyor..."
                      : "Kaydediliyor..."}
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Save className="w-4 h-4 mr-2" />
                    Kaydet
                  </div>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ParentCreateForm;
